import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { format } from 'date-fns'
import PublicShell from '../components/PublicShell'
import LevelExitsNote from '../components/LevelExitsNote'
import ResultsDisclaimer from '../components/ResultsDisclaimer'
import Spinner from '../components/Spinner'
import Tooltip from '../components/Tooltip'
import CreatorFeePanel from '../components/fees/CreatorFeePanel'
import CreatorCourses from '../components/courses/CreatorCourses'
import NewsletterSignup from '../components/newsletter/NewsletterSignup'
import TerminalPanel from '../components/dashboard/TerminalPanel'
import EquityCurveChart, { maxDrawdown, toCurvePoints } from '../components/dashboard/EquityCurveChart'
import DirectionSplitPanel from '../components/trackRecord/DirectionSplitPanel'
import {
  AvgRTile,
  DrawdownReadout,
  EmptyBox,
  OutcomeBadge,
  StatTile,
  WinRateTile,
  formatR,
  pnlClass,
} from '../components/trackRecord/recordUI'
import { TRACK_RECORD_GLOSSARY as GLOSSARY } from '../config/glossary'
import { creatorRecordPath } from '../routes'
import { publicCreatorsAPI } from '../services/api'
import type { CreatorRecord as Record, CreatorRecordCall } from '../types'
import { displaySymbol } from '../utils/displaySymbol'
import { formatPrice } from '../utils/marketPrice'
import { drawdownLine } from '../utils/drawdown'
import { formatPnlPct } from '../utils/pnl'
import { parseApiDate } from '../utils/signalExpiry'

type State = { kind: 'loading' } | { kind: 'missing' } | { kind: 'error' } | { kind: 'ready'; record: Record }

const statusOf = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } })?.response?.status

export const TOO_FEW_TO_JUDGE = 'Too few closed calls to judge'

const day = (value: string | null) => (value ? format(parseApiDate(value), 'MMM d, yyyy') : '—')

const isOpen = (call: CreatorRecordCall) => call.status === 'active' || call.status === 'pending'

/** One call, open or closed, linking to its receipt. Losses and exclusions read like everything else. */
const CallRow = ({ call }: { call: CreatorRecordCall }) => (
  <li>
    <Link
      to={call.receipt_path}
      className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 hover:bg-white/5"
      data-testid="record-call"
    >
      <span className="font-semibold text-gray-100">{displaySymbol(call.symbol)}</span>
      <span className={call.signal_type === 'long' ? 'text-green-300' : 'text-red-300'}>
        {call.signal_type === 'long' ? 'Long' : 'Short'}
      </span>
      <span className="font-mono text-sm text-gray-300">@ {formatPrice(call.entry_price)}</span>
      {isOpen(call) ? (
        <span className="text-sm font-medium text-accent">Open</span>
      ) : (
        <>
          <OutcomeBadge win={call.outcome === 'win' ? true : call.outcome === 'loss' ? false : null} />
          {call.gross_pnl_pct != null && (
            <span className={`font-mono text-sm ${pnlClass(call.gross_pnl_pct)}`}>
              {formatPnlPct(call.gross_pnl_pct)}
              {call.gross_r != null && ` · ${formatR(call.gross_r)}`}
            </span>
          )}
          {call.exit_reason === 'expired' && <span className="text-xs text-gray-400">expired, closed at market</span>}
        </>
      )}
      {call.excluded_from_record && (
        <span className="text-xs text-amber-300" data-testid="record-call-excluded">
          excluded from the record{call.exclusion_note ? `: ${call.exclusion_note}` : ''}
        </span>
      )}
      <span className="ml-auto text-xs text-gray-500">
        #{call.id} · {day(call.created_at)}
      </span>
    </Link>
  </li>
)

const RecordView = ({ record }: { record: Record }) => {
  const { summary, curve } = record
  const closed = summary.closed_signals
  const stats = useMemo(() => {
    const points = toCurvePoints(curve)
    // The API's own running sum, read off the last point, as on the Track Record.
    const last = curve[curve.length - 1]
    return { points, sum: last ? last.cumulative_pnl_pct : 0, drawdown: maxDrawdown(points) }
  }, [curve])

  return (
    <div className="space-y-6" data-testid="creator-record">
      <header>
        <h1 className="text-3xl font-bold text-gray-50" data-testid="record-handle">
          {record.creator.handle ? `@${record.creator.handle}` : 'A deleted account'}
        </h1>
        {record.creator.deleted && (
          <p className="mt-1 text-sm text-gray-400" data-testid="record-deleted">
            This account was deleted. Its calls stay public, as they were locked and scored.
          </p>
        )}
        <p className="mt-1 text-sm text-gray-400">
          Every call this creator locked: at the live price, scored by fixed public rules, win or lose. Nothing here
          is self-reported, and none of it counts in the FinCreator engine&rsquo;s record.
        </p>
        <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm" data-testid="record-header">
          <div className="flex gap-1.5">
            <dt className="text-gray-500">First call</dt>
            <dd className="text-gray-200" data-testid="record-first-call">{day(record.first_call_at)}</dd>
          </div>
          <div className="flex gap-1.5">
            <dt className="text-gray-500">Account created</dt>
            <dd className="text-gray-200" data-testid="record-account-created">{day(record.account_created_at)}</dd>
          </div>
          <div className="flex gap-1.5">
            <dt className="text-gray-500">Open calls</dt>
            <dd className="text-gray-200" data-testid="record-open-calls">{record.open_calls}</dd>
          </div>
        </dl>
        {record.creator.handle && (
          <div className="mt-4">
            <NewsletterSignup handle={record.creator.handle} />
          </div>
        )}
      </header>

      <CreatorCourses creatorId={record.creator.id} dark />

      <section aria-labelledby="creator-record-title">
        <h2 id="creator-record-title" className="mb-2 text-[11px] font-medium uppercase tracking-wider text-gray-500">
          <Tooltip content={GLOSSARY.grossRecord}>Call record · before fees</Tooltip>
          {summary.excluded_signals > 0 && (
            <span className="ml-2 normal-case tracking-normal text-amber-300">
              · {summary.excluded_signals} excluded, listed below
            </span>
          )}
        </h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <WinRateTile
              winRate={summary.win_rate}
              closed={closed}
              wins={summary.wins}
              losses={summary.losses}
              breakevens={summary.breakevens}
              noun="closed"
              interval={summary.win_rate_interval}
            />
            {record.too_few_to_judge && (
              <p className="mt-1 text-xs font-medium text-amber-300" data-testid="too-few-to-judge">
                {TOO_FEW_TO_JUDGE} ({closed} of {record.min_closed_to_judge})
              </p>
            )}
          </div>
          <AvgRTile avgR={summary.avg_r_gross} basis="before fees · per closed call">
            {drawdownLine(summary.drawdown) && (
              <p data-testid="stat-drawdown-r">
                <Tooltip content={GLOSSARY.drawdownR}>{drawdownLine(summary.drawdown)}</Tooltip>
              </p>
            )}
          </AvgRTile>
          <StatTile
            label="Sum of trade returns"
            hint={GLOSSARY.sumOfTradeReturns}
            testId="stat-gross-sum"
            value={closed ? formatPnlPct(stats.sum) : '—'}
            valueClass={closed ? pnlClass(stats.sum) : 'text-gray-500'}
          >
            {closed ? 'before fees · unsized, not account return' : 'no closed calls yet'}
          </StatTile>
        </div>
        <ResultsDisclaimer className="mt-2" />
        <LevelExitsNote className="mt-1" />
      </section>

      <TerminalPanel
        title="Equity curve · before fees"
        tone="indigo"
        subtitle={
          <p className="text-xs text-gray-500">
            Each closed call&rsquo;s result added up in close order: not smoothed, not compounded.
          </p>
        }
        action={closed > 0 ? <DrawdownReadout value={stats.drawdown} unit="points" /> : undefined}
      >
        <div className="h-64 md:h-80">
          {closed === 0 ? (
            <EmptyBox title="No closed calls yet" detail="The first close, win or loss, starts the record." />
          ) : (
            <EquityCurveChart
              points={stats.points}
              ariaLabel={`Equity curve over ${closed} closed calls: ${formatPnlPct(stats.sum)} before fees, ${
                summary.wins
              } wins, ${summary.losses} losses, max drawdown ${stats.drawdown.toFixed(2)} points`}
            />
          )}
        </div>
      </TerminalPanel>

      {closed > 0 && <DirectionSplitPanel summary={summary} noun="calls" engine={false} />}

      {closed > 0 && <CreatorFeePanel creatorId={record.creator.id} />}

      <section aria-labelledby="creator-calls-title">
        <h2 id="creator-calls-title" className="text-sm font-semibold text-gray-100">
          Every call
        </h2>
        <p className="text-xs text-gray-500">
          {record.calls.length} total · open and closed · newest first. Each links to its receipt.
        </p>
        <ul
          className="mt-2 divide-y divide-terminal-border rounded-xl border border-terminal-border bg-terminal-surface"
          data-testid="record-calls"
        >
          {record.calls.map((call) => (
            <CallRow key={call.id} call={call} />
          ))}
        </ul>
      </section>

      <p className="text-xs text-gray-400" data-testid="record-footer">
        Results are before trading fees. Calls are a published record, not financial advice.
      </p>
    </div>
  )
}

/**
 * /c/<handle>: a creator's record, public, no login. Their locked calls only,
 * never an engine signal or another creator's call, with the engine Track
 * Record's figures computed by the same backend functions. The handle is
 * looked up as it is held now, else as it was held: an old handle moves to
 * the owner's current one, and a deleted account's record stays here under
 * the handle it had. A handle nobody ever held is "no calls".
 */
const CreatorRecord = () => {
  const { handle = '' } = useParams<{ handle: string }>()
  const navigate = useNavigate()
  const [state, setState] = useState<State>({ kind: 'loading' })
  // An old handle reaches the same record: move to the one held now.
  const current = state.kind === 'ready' ? state.record.creator.handle : null
  useEffect(() => {
    if (current && current !== handle) navigate(creatorRecordPath(current), { replace: true })
  }, [current, handle, navigate])

  useEffect(() => {
    let cancelled = false
    setState({ kind: 'loading' })
    publicCreatorsAPI
      .get(handle)
      .then((record) => !cancelled && setState({ kind: 'ready', record }))
      .catch((error) => {
        if (!cancelled) setState({ kind: statusOf(error) === 404 ? 'missing' : 'error' })
      })
    return () => {
      cancelled = true
    }
  }, [handle])

  return (
    <PublicShell>
      {state.kind === 'loading' && (
        <div className="flex justify-center py-16" data-testid="record-loading">
          <Spinner />
        </div>
      )}
      {state.kind === 'missing' && (
        <div className="py-16 text-center" data-testid="record-not-found">
          <h1 className="text-xl font-semibold text-gray-100">No calls by @{handle}</h1>
          <p className="mt-2 text-sm text-gray-400">
            Nobody with that handle has locked a call. Handles can change; a call&rsquo;s receipt links to its
            creator&rsquo;s current page.
          </p>
          <div className="mx-auto mt-6 max-w-md text-left">
            <NewsletterSignup handle={handle} />
          </div>
        </div>
      )}
      {state.kind === 'error' && (
        <div className="py-16 text-center" data-testid="record-error">
          <h1 className="text-xl font-semibold text-gray-100">This record could not be loaded</h1>
          <p className="mt-2 text-sm text-gray-400">Try again in a minute.</p>
        </div>
      )}
      {state.kind === 'ready' && <RecordView record={state.record} />}
    </PublicShell>
  )
}

export default CreatorRecord
